/**
 * Category statistics and trending information
 */

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, Star, Download, Heart } from 'lucide-react';

interface CategoryStatsProps {
  type: 'software' | 'holiday' | 'genre' | 'artist';
  value: string;
  stats: {
    totalSequences: number;
    freeSequences: number;
    averageRating: number;
    totalDownloads: number;
    popularTags?: string[];
    trendingSequences?: Array<{
      id: string;
      title: string;
      downloads: number;
      rating: number;
    }>;
  };
  className?: string;
}

export const CategoryStats: React.FC<CategoryStatsProps> = ({
  type,
  value,
  stats,
  className
}) => {
  const {
    totalSequences,
    freeSequences,
    averageRating,
    totalDownloads,
    popularTags = [],
    trendingSequences = []
  } = stats;

  const freePercentage = totalSequences > 0 ? Math.round((freeSequences / totalSequences) * 100) : 0;

  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 ${className}`}>
      {/* Total Sequences */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Total Sequences</p>
              <p className="text-2xl font-bold">{totalSequences.toLocaleString()}</p>
            </div>
            <div className="w-8 h-8 bg-blue-500/10 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-blue-500" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Free Content */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Free Content</p>
              <p className="text-2xl font-bold">{freePercentage}%</p>
              <p className="text-xs text-muted-foreground">{freeSequences} free sequences</p>
            </div>
            <div className="w-8 h-8 bg-green-500/10 rounded-lg flex items-center justify-center">
              <Heart className="w-4 h-4 text-green-500" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Average Rating */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Average Rating</p>
              <p className="text-2xl font-bold">{averageRating.toFixed(1)}</p>
              <div className="flex items-center mt-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`w-3 h-3 ${
                      star <= Math.round(averageRating)
                        ? 'text-yellow-400 fill-current'
                        : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>
            </div>
            <div className="w-8 h-8 bg-yellow-500/10 rounded-lg flex items-center justify-center">
              <Star className="w-4 h-4 text-yellow-500" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Total Downloads */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Total Downloads</p>
              <p className="text-2xl font-bold">{totalDownloads.toLocaleString()}</p>
            </div>
            <div className="w-8 h-8 bg-purple-500/10 rounded-lg flex items-center justify-center">
              <Download className="w-4 h-4 text-purple-500" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Popular Tags */}
      {popularTags.length > 0 && (
        <Card className="md:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Popular Tags</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {popularTags.slice(0, 10).map((tag, index) => (
                <Badge key={index} variant="secondary" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Trending Sequences */}
      {trendingSequences.length > 0 && (
        <Card className="md:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Trending This Week
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {trendingSequences.slice(0, 3).map((sequence, index) => (
                <div key={sequence.id} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center text-xs font-bold text-primary">
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-medium text-sm">{sequence.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {sequence.downloads.toLocaleString()} downloads
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Star className="w-3 h-3 text-yellow-400 fill-current" />
                    <span className="text-xs">{sequence.rating.toFixed(1)}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};